// @ts-nocheck
/**
 * useTTS - 独立的语音生成 Hook
 *
 * 每个组件实例拥有独立的语音状态，解决多弹窗状态冲突问题。
 * 支持音频缓存、播放控制、自动清理。
 *
 * 相关文档: docs/架构设计.md#TTS语音服务
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { apiStream } from '../utils/apiClient';
import { logger } from '../utils/logger';

interface CacheEntry {
  url: string;
  timestamp: number;
}

// 全局音频缓存（跨组件共享，避免重复请求）
const globalAudioCache = new Map<string, CacheEntry>();
const MAX_CACHE_SIZE = 30;
const CACHE_EXPIRE_MS = 30 * 60 * 1000; // 30分钟过期

/**
 * 清理过期缓存项
 */
function cleanExpiredCache(): void {
  const now = Date.now();
  for (const [key, { url, timestamp }] of globalAudioCache.entries()) {
    if (now - timestamp > CACHE_EXPIRE_MS) {
      URL.revokeObjectURL(url);
      globalAudioCache.delete(key);
      logger.debug('[useTTS] 清理过期缓存:', key.slice(0, 20) + '...');
    }
  }
}

// 每5分钟清理一次过期缓存
setInterval(cleanExpiredCache, 5 * 60 * 1000);

export function useTTS() {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // 清理当前音频
  const cleanup = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onplay = null;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.oncanplay = null;
      audioRef.current = null;
    }
    setCurrentAudio(null);
    setIsPlaying(false);
  }, []);

  // 停止播放
  const stopAudio = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // 生成语音
  const generateSpeech = useCallback(async (text: string): Promise<void> => {
    if (!text || text.trim().length === 0) {
      logger.warn('[useTTS] 文本为空，跳过生成');
      return;
    }

    const textKey = text.trim();

    // 停止当前播放
    cleanup();

    // 检查全局缓存（包含过期检查）
    if (globalAudioCache.has(textKey)) {
      const cached = globalAudioCache.get(textKey)!;
      const isExpired = Date.now() - cached.timestamp > CACHE_EXPIRE_MS;

      if (!isExpired) {
        try {
          const audio = new Audio(cached.url);
          audioRef.current = audio;

          audio.onplay = () => {
            setCurrentAudio(audio);
            setIsPlaying(true);
          };
          audio.onended = () => {
            setCurrentAudio(null);
            setIsPlaying(false);
          };
          audio.onerror = () => {
            // 缓存失效，删除并重新生成
            URL.revokeObjectURL(cached.url);
            globalAudioCache.delete(textKey);
            generateSpeech(text);
          };

          await audio.play();
          return;
        } catch (error) {
          logger.log('[useTTS] 缓存音频播放失败，重新生成');
          URL.revokeObjectURL(cached.url);
          globalAudioCache.delete(textKey);
        }
      } else {
        // 过期，清理
        URL.revokeObjectURL(cached.url);
        globalAudioCache.delete(textKey);
      }
    }

    // 开始生成
    setIsGenerating(true);

    try {
      // 使用统一的 API 客户端
      const response = await apiStream('/api/generate-speech-stream', { text: textKey });

      const arrayBuffer = await response.arrayBuffer();

      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('音频数据为空');
      }

      const audioBlob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);

      // 缓存管理（LRU 策略 + 时间戳）
      if (globalAudioCache.size >= MAX_CACHE_SIZE) {
        const firstKey = globalAudioCache.keys().next().value;
        if (firstKey !== undefined) {
          const oldCache = globalAudioCache.get(firstKey)!;
          URL.revokeObjectURL(oldCache.url);
          globalAudioCache.delete(firstKey);
        }
      }
      globalAudioCache.set(textKey, { url: audioUrl, timestamp: Date.now() });

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.oncanplay = () => {
        setIsGenerating(false);
      };

      audio.onplay = () => {
        setCurrentAudio(audio);
        setIsPlaying(true);
      };

      audio.onended = () => {
        setCurrentAudio(null);
        setIsPlaying(false);
      };

      audio.onerror = () => {
        logger.error('[useTTS] 音频播放失败');
        setCurrentAudio(null);
        setIsPlaying(false);
        setIsGenerating(false);
      };

      await audio.play();
    } catch (error) {
      logger.error('[useTTS] 语音生成失败:', error);
      setIsGenerating(false);
      throw error;
    }
  }, [cleanup]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isGenerating,
    isPlaying,
    currentAudio,
    generateSpeech,
    stopAudio,
    cleanup
  };
}

export default useTTS;
